import {
  getObject,
  getOptionalObject,
  getString,
  getOptionalString,
  getInt32,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { DateRange, DateRangeDTO } from "./dateRange";
import { AuditLog, AuditLogDTO } from "./auditLog";
/* eslint-disable import/export */
export abstract class GetAuditLogsRPC implements
  LeoRPC<GetAuditLogsRPC.Request, GetAuditLogsRPC.Response, GetAuditLogsRPC.Errors.Errors>
{
  abstract execute(
    request: GetAuditLogsRPC.Request
  ): Promise<LeoRPCResult<GetAuditLogsRPC.Response, GetAuditLogsRPC.Errors.Errors>>;
}

export namespace GetAuditLogsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    dateRange?: DateRangeDTO | null;
    emailAddress?: string | null;
    eventName?: string | null;
    pageIndex: number;
    itemsPerPage: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetAuditLogsRPCRequest: boolean = true;
  
    readonly dateRange: DateRange | null;
    readonly emailAddress: string | null;
    readonly eventName: string | null;
    readonly pageIndex: number;
    readonly itemsPerPage: number;
  
    static readonly constraints = {
      "emailAddress": {
        "maxSize": 200,
        
        
        
        
      },
      "eventName": {
        "maxSize": 200,
        
        
        
        
      },
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 20,
      }
    }
  
    constructor(
    dateRange: DateRange | null = null, 
    emailAddress: string | null = null, 
    eventName: string | null = null, 
    pageIndex: number, 
    itemsPerPage: number
    ) {
      this.dateRange = dateRange;
      if ( emailAddress !== undefined && emailAddress !== null) {
        Request.validateEmailAddress(emailAddress);
      }
      this.emailAddress = emailAddress;
      if ( eventName !== undefined && eventName !== null) {
        Request.validateEventName(eventName);
      }
      this.eventName = eventName;
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      let dateRange = getOptionalObject(dto, "dateRange");
      if(dateRange !== undefined && dateRange !== null) {
        dateRange = DateRange.fromDTO(getObject(dto, "dateRange", InvalidRequestDateRangeError));
      }
      const emailAddress = getOptionalString(dto, "emailAddress", InvalidRequestEmailAddressError);
      const eventName = getOptionalString(dto, "eventName", InvalidRequestEventNameError);
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      return new Request(
        dateRange,
        emailAddress,
        eventName,
        pageIndex,
        itemsPerPage
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage
      };
      if (this.dateRange !== undefined && this.dateRange !== null){
        dto.dateRange = this.dateRange.toDTO();
      }
      if (this.emailAddress !== undefined && this.emailAddress !== null){
        dto.emailAddress = this.emailAddress;
      }
      if (this.eventName !== undefined && this.eventName !== null){
        dto.eventName = this.eventName;
      }
      return dto;
    }
    private static validateEmailAddress = (emailAddress: string): void => {
      if(emailAddress.length > 200) {
        throw new InvalidRequestEmailAddressMaxSizeError(
          200,
          `Attribute emailAddress is too long. Size is ${emailAddress.length}. Max size is 200.`
        );
      }
        if(emailAddress.trim().length === 0) {
          throw new InvalidRequestEmailAddressNonEmptyError(
            `Attribute emailAddress cannot be an empty string.`
          );
        }
    }
    private static validateEventName = (eventName: string): void => {
      if(eventName.length > 200) {
        throw new InvalidRequestEventNameMaxSizeError(
          200,
          `Attribute eventName is too long. Size is ${eventName.length}. Max size is 200.`
        );
      }
        if(eventName.trim().length === 0) {
          throw new InvalidRequestEventNameNonEmptyError(
            `Attribute eventName cannot be an empty string.`
          );
        }
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 20) {
        throw new InvalidRequestItemsPerPageMinValueError(
          20,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 20.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetAuditLogsRPC(dateRange='${this.dateRange}',emailAddress='${this.emailAddress}',eventName='${this.eventName}',pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    totalItems: number;
    logs: AuditLogDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetAuditLogsResponse: boolean = true;
  
    readonly totalItems: number;
    readonly logs: AuditLog[];
  
  
    constructor(
    totalItems: number, 
    logs: AuditLog[]
    ) {
      this.totalItems = totalItems;
      this.logs = logs;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      const logs = getList<AuditLogDTO, AuditLog>(
        dto,
        "logs",
        AuditLog.fromDTO,
        InvalidResponseLogsError
      );
      return new Response(
        totalItems,
        logs
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          totalItems: this.totalItems,
          logs: this.logs.map((e: AuditLog) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetAuditLogs(totalItems='${this.totalItems}',logs='${this.logs}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidDateRange = "INVALID_DATE_RANGE"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetAuditLogsRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidDateRangeDTO {
      code: string;
    }
    
    export class InvalidDateRange extends LeoRPCError<RPCError> {
      readonly isInvalidDateRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidDateRange;
        super(code);
      }
    
    
      toDTO(): InvalidDateRangeDTO {
        const dto: InvalidDateRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidDateRange.prototype.toString = function toString(): string {
      return `GetAuditLogsRPC.INVALID_DATE_RANGE()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidDateRange;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidDateRangeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidDateRange:
          return new InvalidDateRange();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestDateRangeError extends GetAuditLogsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDateRangeError";
  }
}

export class InvalidRequestEmailAddressError extends GetAuditLogsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressError";
  }
}

export class InvalidRequestEmailAddressMaxSizeError extends InvalidRequestEmailAddressError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMaxSizeError";
  }
}

export class InvalidRequestEmailAddressNonEmptyError extends InvalidRequestEmailAddressError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressNonEmptyError";
  }
}

export class InvalidRequestEventNameError extends GetAuditLogsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEventNameError";
  }
}

export class InvalidRequestEventNameMaxSizeError extends InvalidRequestEventNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEventNameMaxSizeError";
  }
}

export class InvalidRequestEventNameNonEmptyError extends InvalidRequestEventNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEventNameNonEmptyError";
  }
}

export class InvalidRequestPageIndexError extends GetAuditLogsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetAuditLogsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidResponseTotalItemsError extends GetAuditLogsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseLogsError extends GetAuditLogsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseLogsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetAuditLogsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidDateRangeError extends GetAuditLogsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidDateRangeError";
  }
}

